import {
  Breadcrumb,
  BreadcrumbIcon,
  BreadcrumbLink,
  BreadcrumbPage,
  Button,
  useAlert,
} from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeleteSerieVideoLangModal,
  SERIE_VIDEO_LANG_PAGE_SIZE,
  SerieVideoLangTable,
  useDeleteSerieVideoLang,
  useSerieVideoLangList,
} from '@/features/series-video-lang-management';
import { useSerieVideo } from '@/features/series-video-management';
import type { SerieVideoLangViewModel } from '@/shared/api/generated';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export const Route = createFileRoute('/_authenticated/series/$serieId/videos/$videoId/langs/')({
  component: SerieVideoLangListPage,
});

/** Página de listagem de Idiomas do Vídeo. */
export function SerieVideoLangListPage() {
  const { t } = useTranslation('series');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const { serieId: serieIdParam, videoId: videoIdParam } = Route.useParams();
  const serieId = Number(serieIdParam);
  const videoId = Number(videoIdParam);

  const { video } = useSerieVideo(videoId);
  const [page, setPage] = useState(1);
  const { langs, totalCount, totalPages, currentPage, isLoading, isError, refetch } =
    useSerieVideoLangList(videoId, serieId, page);
  const deleteSerieVideoLang = useDeleteSerieVideoLang(videoId);

  const [deleting, setDeleting] = useState<SerieVideoLangViewModel | null>(null);

  const handleDeleteConfirm = () => {
    if (deleting?.id == null) return;
    deleteSerieVideoLang.mutate(
      { videoId, id: deleting.id },
      {
        onSuccess: () =>
          alert({ alertType: 'success', title: t('videoLangs.toast.deleteSuccess') }),
        onError: () => alert({ alertType: 'warning', title: t('videoLangs.toast.deleteError') }),
        onSettled: () => setDeleting(null),
      },
    );
  };

  const addButton = (
    <Button
      hierarchy="primary"
      size="lg"
      leadingIcon={PlusIcon}
      onClick={() =>
        void navigate({
          to: '/series/$serieId/videos/$videoId/langs/create',
          params: { serieId: serieIdParam, videoId: videoIdParam },
        })
      }
    >
      {t('videoLangs.actions.add')}
    </Button>
  );

  return (
    <div className="flex flex-col gap-mini">
      <PageHeader
        title={t('videoLangs.title')}
        subtitle={
          video?.title
            ? t('videoLangs.subtitle', { video: video.title, count: totalCount })
            : t('videoLangs.subtitleNoVideo', { count: totalCount })
        }
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink>
              <Link to="/">
                <BreadcrumbIcon />
              </Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to="/series">{t('videoLangs.breadcrumb.series')}</Link>
            </BreadcrumbLink>
            <BreadcrumbLink>
              <Link to="/series/$serieId/videos" params={{ serieId: serieIdParam }}>
                {t('videoLangs.breadcrumb.videos')}
              </Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('videoLangs.breadcrumb.langs')}</BreadcrumbPage>
          </Breadcrumb>
        }
        actions={addButton}
      />

      {isError ? (
        <div className="flex flex-col items-start gap-nano rounded-md border-hairline border-base-danger-300 bg-ctx-layout-surface p-xxs">
          <p className="text-base-danger-700 ts-paragraph-xs">{t('videoLangs.toast.loadError')}</p>
          <Button hierarchy="secondary" size="md" onClick={() => void refetch()}>
            {t('videoLangs.loadErrorRetry')}
          </Button>
        </div>
      ) : (
        <SerieVideoLangTable
          data={langs}
          isLoading={isLoading}
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={SERIE_VIDEO_LANG_PAGE_SIZE}
          onPageChange={setPage}
          emptyStateAction={addButton}
          onEdit={lang =>
            void navigate({
              to: '/series/$serieId/videos/$videoId/langs/$langId',
              params: { serieId: serieIdParam, videoId: videoIdParam, langId: String(lang.id) },
            })
          }
          onDelete={setDeleting}
        />
      )}

      <DeleteSerieVideoLangModal
        open={deleting !== null}
        langTitle={deleting?.title ?? ''}
        onConfirm={handleDeleteConfirm}
        onOpenChange={open => {
          if (!open) setDeleting(null);
        }}
      />
    </div>
  );
}
